import { useMemo, useState } from "react";
import {
  Badge,
  Button,
  Code,
  Em,
  Flex,
  Link,
  Spinner,
  Text,
} from "@radix-ui/themes";
import { Tabs, TabItem } from "@/v2/components/tabs/Tabs";
import { params } from "@/params";
import { CodeBlock } from "@/components/shared/code-block/CodeBlock";
import { QuackchatConfigurationResponse } from "@/api/quackchat/configuration/useQuackchatPlatformConfiguration";
import { ExternalLinkIcon } from "@radix-ui/react-icons";
import { apiResolver, useApi } from "@/api/useApi";
import { useQuery } from "react-query";
import { AvailableMethods } from "./components/AvailableMethods";

const QUACK_WIDGET_SCRIPT_V1 = `<script>
  (function (q, u, a, c, k, i, o) {
    q[a] =
      q[a] ||
      function () {
        (q[a].q = q[a].q || []).push(arguments);
      };
    i = u.createElement(c);
    i.async = 1;
    i.id = "quack-license";
    i.src = "{{SDK_URL}}/verify.min.js";
    i.setAttribute(a, k);
    o = u.getElementsByTagName(c)[0];
    o.parentNode.insertBefore(i, o);
  })(window, document, "quack", "script", "{{TOKEN}}");
</script>`;

const QUACK_INIT_CODE_V1 = `window.quack?.identify({  userId: 'test-id', email: 'test@email.com' });

window.quack?.showChat();`;

const QUACK_WIDGET_SCRIPT_V2 = `<script
  src="{{SDK_URL}}/quack.min.js"
  onload="window.Quack.load('{{TOKEN}}', {
    onReady: (quack) => {
      quack.identify({
        id: 'test-id',
        email: 'test@email.com'
      });
      quack.showChat();
    }
  })"
></script>`;

export function QuackchatChannelInstallSimulator({
  simulatorId,
}: {
  simulatorId?: string;
}) {
  const { api } = useApi();
  const queryKey = useMemo(
    () =>
      simulatorId
        ? [`quackchat-platform-configuration-${simulatorId}`]
        : ["quackchat-platform-configuration"],
    [simulatorId]
  );
  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () =>
      apiResolver<QuackchatConfigurationResponse>(
        api.get(
          `/platform/quackchat-v2/configuration${
            simulatorId ? `?simulatorId=${simulatorId}` : ""
          }`
        )
      ),
  });

  const [activeTab, setActiveTab] = useState("v2");

  const tabs = useMemo(
    () => [
      {
        value: "v2",
        label: (
          <Flex align="center" gap="2">
            v2
            <Badge color="green" size="1">
              Recommended
            </Badge>
          </Flex>
        ),
      },
      {
        value: "v1",
        label: (
          <Flex align="center" gap="2">
            v1
            <Badge color="gray" size="1">
              Legacy
            </Badge>
          </Flex>
        ),
      },
    ],
    []
  );

  const quackWidgetScriptV1 = useMemo(() => {
    if (data == null) {
      return "";
    }

    return QUACK_WIDGET_SCRIPT_V1.slice()
      .replace("{{SDK_URL}}", params.SDK_URL)
      .replace("{{TOKEN}}", data.token);
  }, [data]);

  const quackWidgetScriptV2 = useMemo(() => {
    if (data == null) {
      return "";
    }

    return QUACK_WIDGET_SCRIPT_V2.slice()
      .replace("{{SDK_URL}}", params.SDK_URL)
      .replace("{{TOKEN}}", data.token);
  }, [data]);

  if (isLoading) {
    return <Spinner size="2" />;
  }

  return (
    <Flex direction={"column"} gap={"24px"}>
      <Text size={"3"} color={"gray"} weight={"medium"} highContrast>
        Install Quack Chat on your site / platform
      </Text>

      <Flex pt={"40px"} direction={"column"} gap={"3"}>
        <Flex justify={"between"} align={"center"}>
          <Flex direction={"column"} gap={"1"}>
            <Text size={"2"} color={"gray"} weight={"medium"} highContrast>
              Copy & Paste the code
            </Text>

            <Text size={"2"} color={"gray"}>
              <Em>
                {"Paste this code before the "}
                <Code color={"indigo"}>&lt;body /&gt;</Code>
                {" tag of every page you wish to embed Quack chat in."}
              </Em>
            </Text>
          </Flex>
          <Link target="_blank" href={`/chatbot-sandbox?t=${data?.token}`}>
            <Button color="gray" highContrast variant="ghost">
              <ExternalLinkIcon />
              See it in Action
            </Button>
          </Link>
        </Flex>

        <Flex direction={"column"} gap={"3"}>
          <Tabs
            tabs={tabs as unknown as TabItem[]}
            value={activeTab}
            onValueChange={setActiveTab}
          />

          {activeTab === "v2" && (
            <Flex direction={"column"} gap={"3"} pt={"3"}>
              <CodeBlock
                code={quackWidgetScriptV2}
                language="html"
                showLineNumbers={false}
              />

              <AvailableMethods />
            </Flex>
          )}

          {activeTab === "v1" && (
            <Flex direction={"column"} gap={"3"} pt={"3"}>
              <CodeBlock
                code={quackWidgetScriptV1}
                language="html"
                showLineNumbers={false}
              />

              <CodeBlock
                code={QUACK_INIT_CODE_V1}
                language="js"
                showLineNumbers={false}
              />
            </Flex>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
}
